'use client';

import { motion } from 'motion/react';
import {
  CheckCheck,
  ChevronRight,
  Clock,
  Mail,
  MapPin,
  Phone,
  type LucideIcon,
} from 'lucide-react';
import { LeadForm } from '@/components/sections/contact-us/form';
import { useSectionMotionVariants } from '@/components/shared/motion';
import { useScrollRevealMotion } from '@/hooks/use-scroll-reveal-motion';
import { SanityInlineText } from '@/components/shared/sanity-text';
import { SectionCtaPair, sectionCtaPairFromContent } from '@/components/shared/section-cta-pair';
import { cn } from '@/lib/utils';
import type { ContactSectionContent } from '@/sanity/lib/sections';
import { trackContactLinkClick } from '@/lib/analytics/contact-link-tracking';
import { SectionLayout } from '@/components/shared/section-layout';

const contactIcons = {
  checkCheck: CheckCheck,
  clock: Clock,
  mail: Mail,
  mapPin: MapPin,
  phone: Phone,
} satisfies Record<string, LucideIcon>;

export default function ContactUs({ content }: { content?: ContactSectionContent | null }) {
  const {
    sectionHeaderItemVariants,
    sectionHeaderRevealVariants,
    sectionSequenceBlockVariants,
    sectionSequenceRevealVariants,
  } = useSectionMotionVariants();
  const scrollReveal = useScrollRevealMotion();
  const { header, ctaButtons } = content ?? {};
  const showHeader = content?.showHeader !== false;
  const showForm = content?.showForm !== false;
  const { primary, secondary } = sectionCtaPairFromContent(content?.showCTA, ctaButtons);
  const contactMethods =
    content?.showContactMethods === false
      ? []
      : (content?.contactMethods ?? []).flatMap((method) => {
          const Icon = contactIcons[method.iconKey as keyof typeof contactIcons];

          if (!Icon || !method.label || !method.value) return [];

          return [
            {
              Icon,
              label: method.label,
              value: method.value,
              href: method.href,
            },
          ];
        });

  if (!content || content.show === false) return null;

  return (
    <SectionLayout
      id="contact-us"
      className="relative scroll-mt-14 sm:scroll-mt-0 bg-background dark:border-y py-6 sm:py-12 xl:py-16 2xl:py-24"
      containerClassName="mx-auto max-w-7xl px-4 sm:px-6 min-[1324px]:px-0"
    >
      <motion.div
        className="relative grid gap-6 lg:grid-cols-12"
        {...scrollReveal}
        variants={sectionSequenceRevealVariants}
      >
        <motion.div variants={sectionHeaderRevealVariants} className="lg:col-span-5 space-y-3">
          {showHeader ? (
            <>
              {header?.tagline ? (
                <motion.div
                  variants={sectionHeaderItemVariants}
                  className="text-[10px] sm:text-xs font-medium uppercase tracking-widest text-[#948B7F]/80 dark:text-[#FCFAD7]/80"
                >
                  {header.tagline}
                </motion.div>
              ) : null}
              {header?.heading ? (
                <motion.h2
                  variants={sectionHeaderItemVariants}
                  className="text-3xl sm:text-5xl leading-tight text-foreground font-light tracking-tight"
                >
                  <SanityInlineText
                    blocks={header.heading}
                    emphasisClassName="italic font-normal text-[#948B7F] dark:text-[#FCFAD7]"
                  />
                </motion.h2>
              ) : null}
              {header?.subheading ? (
                <motion.p
                  variants={sectionHeaderItemVariants}
                  className="text-sm sm:text-base leading-relaxed font-light text-muted-foreground"
                >
                  {header.subheading}
                </motion.p>
              ) : null}
            </>
          ) : null}

          {primary ? (
            <motion.div variants={sectionHeaderItemVariants}>
              <SectionCtaPair
                primary={primary}
                secondary={secondary}
                trackingLocation="contact_us"
              />
            </motion.div>
          ) : null}

          {contactMethods.length ? (
            <motion.div
              variants={sectionHeaderItemVariants}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-1 gap-3 mt-6 sm:mt-12"
            >
              {contactMethods.map(({ Icon, label, value, href }) => {
                const hrefTrimmed = href?.trim() ?? '';
                const isLink = Boolean(hrefTrimmed);
                const card = (
                  <div
                    className={cn(
                      'flex items-center gap-4 rounded-xl border bg-neutral-50 dark:bg-black p-2 sm:p-4 sm:px-5 transition-colors',
                      isLink &&
                        'relative cursor-pointer pr-10 sm:pr-11 hover:border-foreground/20 hover:bg-muted/40',
                    )}
                  >
                    <div className="flex size-8 sm:size-10 shrink-0 items-center justify-center rounded-full sm:rounded-xl bg-neutral-900/10 text-neutral-800 dark:bg-white/10 dark:text-white/80">
                      <Icon className="size-4 shrink-0" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-muted-foreground">
                        {label}
                      </div>
                      <div className="mt-0.5 wrap-break-word text-sm font-medium text-foreground whitespace-pre-line">
                        {value}
                      </div>
                    </div>
                    {isLink ? (
                      <ChevronRight
                        className="pointer-events-none absolute right-3 top-1/2 size-4 shrink-0 -translate-y-1/2 text-muted-foreground"
                        aria-hidden
                      />
                    ) : null}
                  </div>
                );
                return isLink ? (
                  <a
                    key={label}
                    href={hrefTrimmed}
                    className="block rounded-xl outline-offset-2"
                    onClick={() => trackContactLinkClick(hrefTrimmed, 'contact_us')}
                  >
                    {card}
                  </a>
                ) : (
                  <div key={label}>{card}</div>
                );
              })}
            </motion.div>
          ) : null}
        </motion.div>

        {showForm ? (
          <div className="lg:col-span-7 lg:sticky lg:top-32 lg:self-start lg:pl-8 xl:pl-14">
            <motion.div
              variants={sectionSequenceBlockVariants}
              className="relative overflow-hidden rounded-xl border border-border bg-neutral-50 dark:bg-black text-card-foreground shadow-2xl shadow-black/5 px-0! pt-0!"
            >
              <div className="relative z-10">
                <LeadForm
                  content={content}
                  bare
                  cardClassName="sm:rounded-3xl sm:bg-neutral-50 dark:bg-black text-card-foreground"
                />
              </div>
            </motion.div>
          </div>
        ) : null}
      </motion.div>
    </SectionLayout>
  );
}
